import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import {
  WorkflowService,
  DataService,
  WorkflowContextService
} from 'src/app/services';
import { Utilities } from 'src/app/services/utilities';
import { UploadService } from 'src/app/services/upload/upload.service';
import {
  Workflow,
  WorkflowMigrationRequest,
  MigrationProcessing,
  MigrationProcessingNote,
  MigrationExecution,
  MigrationApplicationHistory,
  Client,
  User,
  Document,
  ApplicationDataValidationResult
} from 'src/app/models';
import { forkJoin } from 'rxjs';
import {
  UploadComponent,
  UploadResponse
} from 'src/app/components/upload/upload.component';

interface DataEntityChange {
  templateCode: string;
  action: 'add' | 'remove';
}

interface ActivityChange {
  name: string;
  action: 'add' | 'remove';
  dataEntities: DataEntityChange[];
}

interface MigrationSummary {
  addedActivities: Array<{ name: string; type: string }>;
  removedActivities: Array<{ name: string; type: string }>;
  addedDataEntities: Array<{ activityName: string; templateCode: string }>;
  removedDataEntities: Array<{ activityName: string; templateCode: string }>;
  workflowExists: boolean;
  errorMessages: string[];
  customLists: Array<{
    name: string;
    itemCount: number;
    attributeCount: number;
    alreadyExists: boolean;
  }>;
  roles: Array<{ name: string; alreadyExists: boolean }>;
}

interface MigrationResponse {
  verificationResult: MigrationSummary;
}

interface ApplicationDataValidationStatus {
  isValid: boolean;
  isValidating: boolean;
  recordCount: number;
  errors: string[];
  matchedProperties: string[];
  unmatchedProperties: string[];
  missingRequiredProperties: string[];
}

@Component({
  selector: 'wm-workflow-migration',
  templateUrl: './workflow-migration.component.html',
  styleUrls: ['./workflow-migration.component.css'],
  standalone: false
})
export class WorkflowMigrationComponent implements OnInit {
  @ViewChild('applicationDataUploadComponent', { static: false })
  applicationDataUploadComponent: UploadComponent;
  @ViewChild('csvUploadComponent', { static: false })
  csvUploadComponent: UploadComponent;
  @ViewChild('metadataUploadComponent', { static: false })
  metadataUploadComponent: UploadComponent;

  // Migration Processing List
  migrationProcessings: MigrationProcessing[] = [];
  jurisdictions: Client[] = [];
  selectedJurisdiction: string = null;
  selectedMigrationProcessing: MigrationProcessing = null;
  showMigrationForm: boolean = false;

  // Migration Processing Management
  availableUsers: User[] = [];
  newNote: string = '';
  showChangeOwnerDialog: boolean = false;
  newOwnerId: string = null;

  // Migration Form
  templateWorkflows: Workflow[] = [];
  selectedWorkflowId: string = null;
  workflowName: string = '';
  overwriteWorkflow: boolean = false;
  selectedFile: File = null;
  uploadedFilePath: string = null;
  fileName: string = '';
  loading: boolean = false;
  migrationSummary: MigrationSummary = null;
  hierarchicalActivities: ActivityChange[] = [];
  expandedActivities: Set<string> = new Set();
  summaryVerified: boolean = false;
  error: string = null;
  success: boolean = false;
  applicationDataMigrationError: string = null;
  customListsToUpdate: Set<string> = new Set();
  rolesToUpdate: Set<string> = new Set();
  uploadProgress: number = 0;
  clientId: string = null;
  hasClientContext: boolean = false;
  activeTab: string = 'execution';
  migrationExecutions: MigrationExecution[] = [];
  migrationApplicationHistories: MigrationApplicationHistory[] = [];
  loadingHistory: boolean = false;
  showCsvHelp: boolean = false;
  showMetadataHelp: boolean = false;
  reprocessingMetadata: boolean = false;
  showApplicationDataHelp: boolean = false;
  showDataEntitiesGrid: boolean = false;

  // Application Data Upload and Validation
  applicationDataFilePath: string = null;
  applicationDataValidationStatus: ApplicationDataValidationStatus = null;
  showValidationDetails: boolean = false;
  migratedWorkflow: Workflow = null;

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _workflowSvc: WorkflowService,
    private _dataSvc: DataService,
    private _context: WorkflowContextService,
    private _uploadSvc: UploadService
  ) {}

  ngOnInit() {
    // Get client ID from route parameters
    this._route.parent?.parent?.paramMap.subscribe(params => {
      this.clientId = params.get('clientId');
      this.hasClientContext = !!this.clientId;

      if (this.hasClientContext) {
        this.selectedJurisdiction = this.clientId;
        this.loadMigrationProcessings();
      } else {
        this.loadJurisdictions();
      }
    });

    this.loadTemplateWorkflows();
  }

  loadJurisdictions() {
    this.loading = true;
    this._dataSvc.getClients().subscribe({
      next: clients => {
        this.jurisdictions = clients;
        this.loading = false;
      },
      error: err => {
        this.error = 'Failed to load jurisdictions: ' + err.message;
        this.loading = false;
      }
    });
  }

  onJurisdictionSelected(jurisdictionId: string) {
    this.selectedJurisdiction = jurisdictionId;
    if (jurisdictionId) {
      this.loadMigrationProcessings();
    }
  }

  loadMigrationProcessings() {
    if (!this.selectedJurisdiction) {
      return;
    }

    this.loading = true;
    this._dataSvc
      .getAvailableMigrationProcessings(this.selectedJurisdiction)
      .subscribe({
        next: processings => {
          this.migrationProcessings = processings;
          this.loading = false;
        },
        error: err => {
          this.error = 'Failed to load migration processings: ' + err.message;
          this.loading = false;
        }
      });
  }

  loadTemplateWorkflows() {
    this.loading = true;
    this._workflowSvc.getTemplateLibraryWorkflows().subscribe({
      next: workflows => {
        this.templateWorkflows = workflows;
        this.loading = false;
      },
      error: err => {
        this.error = 'Failed to load template workflows: ' + err.message;
        this.loading = false;
      }
    });
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        this.error = 'Please select a CSV file';
        this.selectedFile = null;
        this.uploadedFilePath = null;
        this.fileName = '';
        return;
      }

      this.selectedFile = file;
      this.fileName = file.name;
      this.error = null;
      this.migrationSummary = null;
      this.summaryVerified = false;
      this.uploadedFilePath = null;

      // Upload the file immediately
      this.uploadFile(file);
    }
  }

  uploadFile(file: File) {
    // Upload the CSV file to cloud storage first using the UploadService
    // The uploaded file path will then be passed to the backend API
    this.loading = true;
    this.uploadProgress = 0;

    const fileSet = new Set<File>();
    fileSet.add(file);

    // Upload to workflow-migration folder
    const uploadResults = this._uploadSvc.upload(fileSet, 'workflow-migration');

    // Subscribe to the upload progress for this file
    const fileUpload = uploadResults[file.name];
    if (fileUpload) {
      fileUpload.progress.subscribe({
        next: progress => {
          if (progress.path) {
            // Upload complete - save the cloud path
            this.uploadedFilePath = progress.path;
            this.loading = false;
          } else if (progress.uploadTotal && progress.uploaded) {
            // Update progress percentage
            this.uploadProgress = Math.round(
              (progress.uploaded / progress.uploadTotal) * 100
            );
          }
        },
        error: err => {
          this.error =
            'File upload failed: ' + (err.message || 'Unknown error');
          this.loading = false;
          this.uploadedFilePath = null;
        }
      });
    }
  }

  onWorkflowSelected(workflowId: string) {
    this.selectedWorkflowId = workflowId;
    this.migrationSummary = null;
    this.summaryVerified = false;
    this.error = null;
  }

  canGenerateSummary(): boolean {
    return (
      this.selectedMigrationProcessing !== null &&
      this.workflowName !== null &&
      this.workflowName.trim() !== '' &&
      !this.loading
    );
  }

  canExecuteMigration(): boolean {
    return (
      this.summaryVerified && this.migrationSummary !== null && !this.loading
    );
  }

  generateSummary() {
    if (!this.selectedMigrationProcessing) {
      this.error = 'No migration processing selected';
      return;
    }

    if (!this.workflowName || this.workflowName.trim() === '') {
      this.error = 'Please enter a workflow name';
      return;
    }

    this.loading = true;
    this.error = null;
    this.migrationSummary = null;
    this.customListsToUpdate.clear();

    const request = new WorkflowMigrationRequest({
      clientId: this.clientId,
      workflowName: this.workflowName.trim(),
      overwriteExistingWorkflow: this.overwriteWorkflow,
      templateWorkflowId: this.selectedWorkflowId,
      isVerified: false,
      migrationProcessingId: this.selectedMigrationProcessing?.id
    });

    this._dataSvc.migrateWorkflow(request).subscribe({
      next: (response: MigrationResponse) => {
        this.migrationSummary = response.verificationResult;
        this.buildHierarchicalView();
        this.loading = false;
      },
      error: err => {
        this.error =
          'Failed to generate summary: ' + (err.error?.message || err.message);
        this.loading = false;
      }
    });
  }

  executeMigration() {
    if (!this.selectedMigrationProcessing || !this.summaryVerified) {
      this.error = 'Please verify the summary before executing migration';
      return;
    }

    if (!this.workflowName || this.workflowName.trim() === '') {
      this.error = 'Please enter a workflow name';
      return;
    }

    this.loading = true;
    this.error = null;

    const request = new WorkflowMigrationRequest({
      clientId: this.clientId,
      workflowName: this.workflowName.trim(),
      overwriteExistingWorkflow: this.overwriteWorkflow,
      templateWorkflowId: this.selectedWorkflowId,
      isVerified: true,
      migrationProcessingId: this.selectedMigrationProcessing?.id,
      customListsToUpdate: Array.from(this.customListsToUpdate),
      rolesToUpdate: Array.from(this.rolesToUpdate)
    });

    this._dataSvc.migrateWorkflow(request).subscribe({
      next: (result: any) => {
        this.success = true;
        this.loading = false;

        // Close the migration form and reload the migration processing details
        setTimeout(() => {
          this.showMigrationForm = false;
          if (this.selectedMigrationProcessing) {
            // Reload the migration processing to get updated status
            this._dataSvc
              .getMigrationProcessing(this.selectedMigrationProcessing.id)
              .subscribe({
                next: processing => {
                  this.selectedMigrationProcessing = processing;
                  this.loadMigrationProcessingNotes();
                },
                error: err => {
                  this.error =
                    'Failed to reload migration processing: ' + err.message;
                }
              });
          }
        }, 2000);
      },
      error: err => {
        this.error = 'Migration failed: ' + (err.error?.message || err.message);
        this.loading = false;
      }
    });
  }

  reset() {
    this.selectedWorkflowId = null;
    this.workflowName = '';
    this.overwriteWorkflow = false;
    this.selectedFile = null;
    this.uploadedFilePath = null;
    this.fileName = '';
    this.migrationSummary = null;
    this.summaryVerified = false;
    this.error = null;
    this.success = false;
    this.applicationDataFilePath = null;
    this.applicationDataValidationStatus = null;
    this.showValidationDetails = false;
    this.migratedWorkflow = null;
    this.customListsToUpdate.clear();
    this.rolesToUpdate.clear();

    // Reset file input
    const fileInput = document.getElementById(
      'csvFileInput'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  getSelectedWorkflowName(): string {
    if (!this.selectedWorkflowId || this.templateWorkflows.length === 0) {
      return 'None (Create new workflow)';
    }

    const workflow = this.templateWorkflows.find(
      w => w.id === this.selectedWorkflowId
    );
    return workflow ? workflow.version?.name || workflow.name : 'Unknown';
  }

  hasErrors(): boolean {
    return this.migrationSummary?.errorMessages?.length > 0;
  }

  hasWarnings(): boolean {
    return false; // No longer tracking warnings in the new structure
  }

  buildHierarchicalView() {
    if (!this.migrationSummary) {
      this.hierarchicalActivities = [];
      return;
    }

    const activityMap = new Map<string, ActivityChange>();

    // Add all added activities
    this.migrationSummary.addedActivities?.forEach(activity => {
      if (!activity) return;
      activityMap.set(activity.name, {
        name: activity.name,
        action: 'add',
        dataEntities: []
      });
    });

    // Add all removed activities
    this.migrationSummary.removedActivities?.forEach(activity => {
      if (!activity) return;
      activityMap.set(activity.name, {
        name: activity.name,
        action: 'remove',
        dataEntities: []
      });
    });

    // Add data entities to their respective activities
    this.migrationSummary.addedDataEntities?.forEach(entity => {
      let activity = activityMap.get(entity.activityName);
      if (!activity) {
        // Create activity if it doesn't exist (existing activity with new entities)
        activity = {
          name: entity.activityName,
          action: 'add',
          dataEntities: []
        };
        activityMap.set(entity.activityName, activity);
      }
      activity.dataEntities.push({
        templateCode: entity.templateCode,
        action: 'add'
      });
    });

    // Add removed data entities
    this.migrationSummary.removedDataEntities?.forEach(entity => {
      let activity = activityMap.get(entity.activityName);
      if (!activity) {
        // Create activity if it doesn't exist (existing activity with removed entities)
        activity = {
          name: entity.activityName,
          action: 'add',
          dataEntities: []
        };
        activityMap.set(entity.activityName, activity);
      }
      activity.dataEntities.push({
        templateCode: entity.templateCode,
        action: 'remove'
      });
    });

    this.hierarchicalActivities = Array.from(activityMap.values());
  }

  toggleActivity(activityName: string) {
    if (this.expandedActivities.has(activityName)) {
      this.expandedActivities.delete(activityName);
    } else {
      this.expandedActivities.add(activityName);
    }
  }

  isActivityExpanded(activityName: string): boolean {
    return this.expandedActivities.has(activityName);
  }

  hasExistingCustomLists(): boolean {
    return (
      this.migrationSummary?.customLists?.some(cl => cl.alreadyExists) ?? false
    );
  }

  toggleCustomListUpdate(listName: string) {
    if (this.customListsToUpdate.has(listName)) {
      this.customListsToUpdate.delete(listName);
    } else {
      this.customListsToUpdate.add(listName);
    }
  }

  isCustomListSelectedForUpdate(listName: string): boolean {
    return this.customListsToUpdate.has(listName);
  }

  hasExistingRoles(): boolean {
    return this.migrationSummary?.roles?.some(r => r.alreadyExists) ?? false;
  }

  toggleRoleUpdate(roleName: string) {
    if (this.rolesToUpdate.has(roleName)) {
      this.rolesToUpdate.delete(roleName);
    } else {
      this.rolesToUpdate.add(roleName);
    }
  }

  isRoleSelectedForUpdate(roleName: string): boolean {
    return this.rolesToUpdate.has(roleName);
  }

  // Migration Processing Workflow Methods
  selectMigrationProcessing(processing: MigrationProcessing) {
    this.selectedMigrationProcessing = processing;
    this.showMigrationForm = false;
    this.showDataEntitiesGrid = false;
    this.reset();
    this.success = this.selectedMigrationProcessing.workflowMigratedOn != null;

    // Load the application data file path if it exists
    if (this.selectedMigrationProcessing.applicationDataFilePath) {
      this.applicationDataFilePath = this.selectedMigrationProcessing.applicationDataFilePath;

      // Validate application data if it exists and workflow has been migrated
      if (this.selectedMigrationProcessing.migratedWorkflowId) {
        this.validateApplicationData(
          this.selectedMigrationProcessing.applicationDataFilePath
        );
      }
    }

    this.loadAvailableUsers();
    this.loadMigrationProcessingNotes();
    this.loadMigrationExecutions();
  }

  createNewMigrationProcessing() {
    if (this.hasClientContext) {
      this._router.navigate(['../workflow-migration/create'], {
        relativeTo: this._route
      });
    } else {
      this._router.navigate(['/settings/workflow-migration/create']);
    }
  }

  backToList() {
    this.selectedMigrationProcessing = null;
    this.showMigrationForm = false;
    this.reset();
    this.loadMigrationProcessings();
  }

  addNote() {
    if (!this.newNote.trim()) {
      return;
    }

    if (!this.selectedMigrationProcessing) {
      this.error = 'No migration processing selected';
      return;
    }

    this.loading = true;

    const note = new MigrationProcessingNote({
      migrationProcessingId: this.selectedMigrationProcessing.id,
      note: this.newNote
    });

    this._dataSvc.saveMigrationProcessingNote(note).subscribe({
      next: createdNote => {
        if (!this.selectedMigrationProcessing.migrationProcessingNotes) {
          this.selectedMigrationProcessing.migrationProcessingNotes = [];
        }
        this.selectedMigrationProcessing.migrationProcessingNotes.unshift(
          createdNote
        );
        this.newNote = '';
        this.loading = false;
      },
      error: err => {
        this.error = 'Failed to add note: ' + err.message;
        this.loading = false;
      }
    });
  }

  changeOwner(newOwnerId: string) {
    if (!this.selectedMigrationProcessing) {
      return;
    }

    this.loading = true;

    this._dataSvc
      .changeMigrationProcessingOwner(
        this.selectedMigrationProcessing.id,
        newOwnerId
      )
      .subscribe({
        next: updatedProcessing => {
          this.selectedMigrationProcessing = updatedProcessing;
          this.loading = false;
          this.showChangeOwnerDialog = false;
          this.newOwnerId = null;
        },
        error: err => {
          this.error = 'Failed to change owner: ' + err.message;
          this.loading = false;
        }
      });
  }

  confirmChangeOwner() {
    if (!this.newOwnerId) {
      return;
    }
    this.changeOwner(this.newOwnerId);
  }

  cancelChangeOwner() {
    this.showChangeOwnerDialog = false;
    this.newOwnerId = null;
  }

  markAsComplete() {
    if (!this.selectedMigrationProcessing) {
      return;
    }

    if (
      !confirm(
        'Are you sure you want to mark this migration as complete? This action cannot be undone.'
      )
    ) {
      return;
    }

    this.loading = true;

    this._dataSvc
      .completeMigrationProcessing(this.selectedMigrationProcessing.id)
      .subscribe({
        next: () => {
          this.success = true;
          this.selectedMigrationProcessing = null;
          this.loadMigrationProcessings();
          this.loading = false;
        },
        error: err => {
          this.error = 'Failed to mark as complete: ' + err.message;
          this.loading = false;
        }
      });
  }

  deleteMigrationProcessing() {
    if (!this.selectedMigrationProcessing) {
      return;
    }

    if (
      !confirm(
        'Are you sure you want to delete this migration processing record? This action will hide the record but can be recovered if needed.'
      )
    ) {
      return;
    }

    this.loading = true;

    this._dataSvc
      .deleteMigrationProcessing(this.selectedMigrationProcessing.id)
      .subscribe({
        next: () => {
          this.success = true;
          this.selectedMigrationProcessing = null;
          this.loadMigrationProcessings();
          this.loading = false;
        },
        error: err => {
          this.error = 'Failed to delete migration: ' + err.message;
          this.loading = false;
        }
      });
  }

  deleteMigrationProcessingById(processingId: string, event: Event) {
    event.stopPropagation();

    if (
      !confirm(
        'Are you sure you want to delete this migration processing record? This action will hide the record but can be recovered if needed.'
      )
    ) {
      return;
    }

    this.loading = true;

    this._dataSvc.deleteMigrationProcessing(processingId).subscribe({
      next: () => {
        this.success = true;
        this.loadMigrationProcessings();
        this.loading = false;
      },
      error: err => {
        this.error = 'Failed to delete migration: ' + err.message;
        this.loading = false;
      }
    });
  }

  loadAvailableUsers() {
    this._dataSvc.getDoAnythingUsers().subscribe({
      next: users => {
        this.availableUsers = users;
      },
      error: err => {
        console.error('Failed to load users:', err);
        this.availableUsers = [];
      }
    });
  }

  loadMigrationExecutions() {
    if (!this.selectedMigrationProcessing?.id) {
      return;
    }

    this.loadingHistory = true;
    forkJoin({
      executions: this._dataSvc.getMigrationExecutions(
        this.selectedMigrationProcessing.id
      ),
      applicationHistories: this._dataSvc.getMigrationApplicationHistories(
        this.selectedMigrationProcessing.id
      )
    }).subscribe({
      next: ({ executions, applicationHistories }) => {
        this.migrationExecutions = executions || [];
        this.migrationApplicationHistories = applicationHistories || [];
        this.loadingHistory = false;
      },
      error: err => {
        console.error('Failed to load migration history:', err);
        this.migrationExecutions = [];
        this.migrationApplicationHistories = [];
        this.loadingHistory = false;
      }
    });
  }

  downloadMetadataFile(): void {
    if (!this.selectedMigrationProcessing?.id) {
      return;
    }
    this._dataSvc
      .downloadMigrationMetadataFile(this.selectedMigrationProcessing.id)
      .subscribe({
        next: (filePath: string) => {
          if (filePath) {
            const downloadUrl =
              Utilities.getRootURL() +
              '/home/GetAzureDocument?documentPath=' +
              escape(filePath) +
              '&download=true';
            window.open(downloadUrl, '_blank');
          } else {
            this.error = 'No metadata file is available to download.';
          }
        },
        error: err => {
          this.error =
            'Failed to download metadata file: ' +
            (err.error?.message || err.message);
        }
      });
  }

  downloadExecutionMetadataFile(filePath: string): void {
    if (!filePath) {
      return;
    }

    const downloadUrl =
      Utilities.getRootURL() +
      '/home/GetAzureDocument?documentPath=' +
      escape(filePath) +
      '&download=true';
    window.open(downloadUrl, '_blank');
  }

  openMigratedApplication(workflowApplicationId: string): void {
    if (!workflowApplicationId) {
      return;
    }

    const applicationUrl = this._router.serializeUrl(
      this._router.createUrlTree([
        '/application/workflow-application',
        workflowApplicationId
      ])
    );

    window.open(applicationUrl, '_blank');
  }

  loadMigrationProcessingNotes() {
    if (!this.selectedMigrationProcessing?.id) {
      return;
    }

    this._dataSvc
      .getMigrationProcessingNotes(this.selectedMigrationProcessing.id)
      .subscribe({
        next: notes => {
          if (this.selectedMigrationProcessing) {
            this.selectedMigrationProcessing.migrationProcessingNotes = notes;
          }
        },
        error: err => {
          console.error('Failed to load notes:', err);
          if (this.selectedMigrationProcessing) {
            this.selectedMigrationProcessing.migrationProcessingNotes = [];
          }
        }
      });
  }

  // Application Data Upload and Validation Methods
  onApplicationDataUploaded(response: UploadResponse) {
    this.applicationDataFilePath = response.path;

    // Save the path to the migration processing record
    if (this.selectedMigrationProcessing) {
      this.selectedMigrationProcessing.applicationDataFilePath = response.path;
      this._dataSvc
        .saveMigrationProcessing(this.selectedMigrationProcessing)
        .subscribe({
          next: updated => {
            console.log(
              'Application data file path saved to migration processing record'
            );
            this.selectedMigrationProcessing = updated;
          },
          error: err => {
            console.error('Failed to save application data file path:', err);
            this.error =
              'Saved file but failed to update migration record: ' +
              err.message;
          }
        });
    }

    this.validateApplicationData(response.path);
  }

  onApplicationDataDeleted(document: Document) {
    this.applicationDataFilePath = null;
    this.applicationDataValidationStatus = null;
    this.showValidationDetails = false;

    // Clear the path from the migration processing record
    if (this.selectedMigrationProcessing) {
      this.selectedMigrationProcessing.applicationDataFilePath = null;
      this._dataSvc
        .saveMigrationProcessing(this.selectedMigrationProcessing)
        .subscribe({
          next: updated => {
            console.log(
              'Application data file path cleared from migration processing record'
            );
            this.selectedMigrationProcessing = updated;
          },
          error: err => {
            console.error('Failed to clear application data file path:', err);
          }
        });
    }
  }

  loadMigratedWorkflowForValidation(workflowId: string) {
    this._dataSvc.getWorkflow(workflowId, false, false).subscribe({
      next: workflow => {
        this.migratedWorkflow = workflow;
      },
      error: err => {
        console.error('Failed to load migrated workflow:', err);
      }
    });
  }

  validateApplicationData(filePath: string) {
    this.applicationDataValidationStatus = {
      isValid: false,
      isValidating: true,
      recordCount: 0,
      errors: [],
      matchedProperties: [],
      unmatchedProperties: [],
      missingRequiredProperties: []
    };

    // Call API to validate - use workflow from migration processing or migrated workflow
    const workflowId =
      this.migratedWorkflow?.id ||
      this.selectedMigrationProcessing?.migratedWorkflowId;

    if (!workflowId) {
      this.applicationDataValidationStatus = {
        isValid: false,
        isValidating: false,
        recordCount: 0,
        errors: [
          'Cannot validate: No workflow ID available. Please complete the migration first.'
        ],
        matchedProperties: [],
        unmatchedProperties: [],
        missingRequiredProperties: []
      };
      return;
    }

    this._dataSvc
      .validateMigratedApplicationData(filePath, workflowId)
      .subscribe({
        next: result => {
          this.applicationDataValidationStatus = {
            isValid: result.isValid,
            isValidating: false,
            recordCount: result.recordCount,
            errors: result.errors || [],
            matchedProperties: result.matchedProperties || [],
            unmatchedProperties: result.unmatchedProperties || [],
            missingRequiredProperties: result.missingRequiredProperties || []
          };
        },
        error: err => {
          this.applicationDataValidationStatus = {
            isValid: false,
            isValidating: false,
            recordCount: 0,
            errors: ['Failed to validate application data: ' + err.message],
            matchedProperties: [],
            unmatchedProperties: [],
            missingRequiredProperties: []
          };
        }
      });
  }

  canPerformMigration(): boolean {
    return true; //return this.selectedMigrationProcessing?.currentOwnerId === this._context.user?.id;
  }

  onMetadataFileUploaded(response: UploadResponse) {
    if (this.selectedMigrationProcessing) {
      this.reprocessingMetadata = true;
      this.success = false;
      this.error = null;
      this.selectedMigrationProcessing.metaDataFilePath = response.path;
      // Save the updated migration processing
      this._dataSvc
        .saveMigrationProcessing(this.selectedMigrationProcessing)
        .subscribe({
          next: updated => {
            this.selectedMigrationProcessing = updated;

            // Automatically parse metadata to create data entities and routes
            this._dataSvc
              .parseMetadataFile(this.selectedMigrationProcessing.id)
              .subscribe({
                next: result => {
                  this.reprocessingMetadata = false;
                  console.log(
                    'Metadata parsed successfully, created',
                    result.dataEntities?.length || 0,
                    'entities and',
                    result.routes?.length || 0,
                    'routes'
                  );

                  // Display validation errors and warnings
                  if (
                    result.validationErrors &&
                    result.validationErrors.length > 0
                  ) {
                    this.error = `Metadata validation failed with ${result.validationErrors.length} error(s). See console for details.`;
                    console.error(
                      'Metadata validation errors:',
                      result.validationErrors
                    );
                  } else {
                    this.success = true;
                    setTimeout(() => (this.success = false), 3000);
                  }

                  if (
                    result.validationWarnings &&
                    result.validationWarnings.length > 0
                  ) {
                    console.warn(
                      'Metadata validation warnings:',
                      result.validationWarnings
                    );
                  }

                  this.onDataEntitiesChanged();
                },
                error: err => {
                  this.reprocessingMetadata = false;
                  console.error('Failed to parse metadata:', err);
                  this.error =
                    'Metadata file uploaded but failed to parse: ' +
                    err.message;
                }
              });
          },
          error: err => {
            this.reprocessingMetadata = false;
            this.error = 'Failed to update metadata file: ' + err.message;
          }
        });
    }
  }

  onDataEntitiesChanged() {
    // Refresh the migration processing or handle entity changes
    console.log('Data entities have been updated');
  }

  onMetadataFileDeleted(document: Document) {
    if (this.selectedMigrationProcessing) {
      this.selectedMigrationProcessing.metaDataFilePath = null;
      this._dataSvc
        .saveMigrationProcessing(this.selectedMigrationProcessing)
        .subscribe({
          next: updated => {
            this.selectedMigrationProcessing = updated;
          },
          error: err => {
            this.error = 'Failed to remove metadata file: ' + err.message;
          }
        });
    }
  }

  startMigration() {
    if (!this.selectedMigrationProcessing) {
      return;
    }
    this.success = false;
    this.showMigrationForm = true;
  }

  migrateApplicationData() {
    if (
      !this.selectedMigrationProcessing ||
      !this.selectedMigrationProcessing.applicationDataFilePath
    ) {
      this.error = 'No application data file available';
      return;
    }

    if (!this.applicationDataValidationStatus?.isValid) {
      this.error = 'Application data must be validated before migration';
      return;
    }

    this.loading = true;
    this.error = null;
    this.applicationDataMigrationError = null;

    this._dataSvc
      .migrateApplicationData(this.selectedMigrationProcessing.id)
      .subscribe({
        next: result => {
          this.loading = false;
          this.selectedMigrationProcessing =
            result.migrationProcessing || this.selectedMigrationProcessing;
          this.success = true;
          this.applicationDataMigrationError = null;
          setTimeout(() => (this.success = false), 5000);
        },
        error: err => {
          this.loading = false;

          let errorMsg = 'Unknown error occurred';

          // Try to extract the most detailed error message
          if (err.error) {
            if (typeof err.error === 'string') {
              errorMsg = err.error;
            } else if (err.error.message) {
              errorMsg = err.error.message;
            } else if (err.error.ExceptionMessage) {
              errorMsg = err.error.ExceptionMessage;
            } else if (err.error.Message) {
              errorMsg = err.error.Message;
            }
          } else if (err.message) {
            errorMsg = err.message;
          } else if (err.statusText) {
            errorMsg = err.statusText;
          }

          this.applicationDataMigrationError = errorMsg;
          console.error('Application data migration error:', err);
        }
      });
  }
}
